import { ServiceLocationObjectType } from '@abvehicles/types/api-types';
import { IssueLength, IssueType } from '@abvehicles/types/issue-types';
import { PartitionFileObjectsType } from '@utils';
import { StateCreator } from 'zustand';

// fill in mock data for testing -> to remove in production
export const emptyServiceLocationDetails = {
  address_line_1: '',
  address_line_2: '',
  country: '',
  county: '',
  easting: '',
  latitude: null,
  longitude: null,
  northing: '',
  postcode: '',
  town_city: '',
  UPRN: '',
  USRN: '',
  additional_details: '',
};

// This is a placeholder the states will be updated during development.
export interface IssueDetailsSlice {
  isInvolvedInAccident: {
    isInvolvedInAccident: boolean | null;
    hasContactedPolice: boolean | null;
  };
  updateIsInvolvedInAccident: (isInvolvedInAccident: {
    isInvolvedInAccident: boolean | null;
    hasContactedPolice: boolean | null;
  }) => void;
  isLeftInDangerousPosition: {
    isLeftInDangerousPosition: boolean | null;
    hasContactedPolice: boolean | null;
  };
  updateIsLeftInDangerousPosition: (isLeftInDangerousPosition: {
    isLeftInDangerousPosition: boolean | null;
    hasContactedPolice: boolean | null;
  }) => void;
  isLeaking: boolean | null;
  updateIsLeaking: (isLeaking: boolean | null) => void;
  wantConfirmationEmail: boolean | null;
  updateWantConfirmationEmail: (wantConfirmationEmail: boolean | null) => void;
  issueType: {
    issueType: IssueType | null;
    additionalInfo: string;
  };
  updateIssueType: (issueType: {
    issueType: IssueType | null;
    additionalInfo: string;
  }) => void;
  serviceLocationDetails: ServiceLocationObjectType;
  updateServiceLocationDetails: (
    serviceLocationDetails: ServiceLocationObjectType,
  ) => void;
  issueLength: IssueLength | null;
  updateIssueLength: (issueLength: IssueLength | null) => void;
  isPhotoLocationStored: boolean;
  updateIsPhotoLocationStored: (isPhotoLocationStored: boolean) => void;
  uploadedImages: string[];
  uploadedImagesFileName: string[];
  uploadedImagesFileType: string[];
  uploadedImagesBlob: {
    blob_uri: string;
    blob_endpoint: string;
    filename: string;
  }[];
  vehicleDetails: {
    registrationNumber: string;
    make: string;
    model: string;
    colour: string;
  };
  updateVehicleDetails: (vehicleDetails: {
    registrationNumber: string;
    make: string;
    model: string;
    colour: string;
  }) => void;
  fileDetailsObjects: { name: string; path: string }[];
  updateUploadedImages: (uploadedImages: PartitionFileObjectsType) => void;
  updateUploadedImagesBlobs: (
    uploadedImagesBlob: {
      blob_uri: string;
      blob_endpoint: string;
      filename: string;
    }[],
  ) => void;
  resetIssueDetails: () => void;
  fileWithLoadedCoordinates: string;
  updateFileWithLoadedCoordinates: (fileWithLoadedCoordinates: string) => void;
}
export const createIssueDetailsSlice: StateCreator<
  IssueDetailsSlice,
  [],
  [],
  IssueDetailsSlice
> = (set, get) => ({
  issueType: {
    issueType: null,
    additionalInfo: '',
  },
  isInvolvedInAccident: {
    isInvolvedInAccident: null,
    hasContactedPolice: null,
  },
  isLeftInDangerousPosition: {
    isLeftInDangerousPosition: null,
    hasContactedPolice: null,
  },
  isLeaking: null,
  isPhotoLocationStored: false,
  uploadedImages: [],
  uploadedImagesFileName: [],
  uploadedImagesFileType: [],
  uploadedImagesBlob: [],
  fileDetailsObjects: [],
  serviceLocationDetails: emptyServiceLocationDetails,
  wantConfirmationEmail: null,
  issueLength: null,
  fileWithLoadedCoordinates: '',
  vehicleDetails: {
    registrationNumber: '',
    make: '',
    model: '',
    colour: '',
  },
  updateIsPhotoLocationStored: (isPhotoLocationStored) =>
    set(() => ({ isPhotoLocationStored })),
  updateVehicleDetails: (vehicleDetails) => set(() => ({ vehicleDetails })),
  updateFileWithLoadedCoordinates: (fileWithLoadedCoordinates: string) =>
    set(() => ({ fileWithLoadedCoordinates })),
  updateWantConfirmationEmail: (wantConfirmationEmail) =>
    set(() => ({ wantConfirmationEmail })),
  updateServiceLocationDetails: (serviceLocationDetails) =>
    set(() => ({ serviceLocationDetails })),
  updateIssueLength: (issueLength) => set(() => ({ issueLength })),
  updateUploadedImages: (uploadedFileParts: PartitionFileObjectsType) => {
    const {
      uploadedImages,
      uploadedImagesFileName,
      uploadedImagesFileType,
      uploadedImagesBlob,
      geoDataArrays,
    } = uploadedFileParts;
    const fileDetailsObjects = uploadedImagesFileName.map(
      (filename, index) => ({
        name: filename,
        path: uploadedImagesBlob[index].blob_uri,
      }),
    );
    const { serviceLocationDetails, updateFileWithLoadedCoordinates } = get();
    // update the service location details with the first geo data found
    if (
      !serviceLocationDetails.latitude &&
      !serviceLocationDetails.longitude &&
      geoDataArrays.length > 0
    ) {
      geoDataArrays.some((geoData) => {
        if (
          geoData.latitude !== null &&
          geoData.longitude !== null &&
          geoData.fileName !== null
        ) {
          updateFileWithLoadedCoordinates(geoData.fileName);
          set((state) => ({
            serviceLocationDetails: {
              ...state.serviceLocationDetails,
              latitude: geoData.latitude,
              longitude: geoData.longitude,
            },
          }));
          // break the loop if the first geo data is found
          return true;
        }
        // continue the loop
        return false;
      });
    }
    // update the service location details with the first geo data found
    set({ uploadedImages });
    set({ uploadedImagesFileName });
    set({ uploadedImagesFileType });
    set({ uploadedImagesBlob });
    set({ fileDetailsObjects });
  },
  updateUploadedImagesBlobs: (uploadedImagesBlob) =>
    set(() => ({ uploadedImagesBlob })),
  updateIsInvolvedInAccident: (isInvolvedInAccident) =>
    set(() => ({ isInvolvedInAccident })),
  updateIsLeftInDangerousPosition: (isLeftInDangerousPosition) =>
    set(() => ({ isLeftInDangerousPosition })),
  updateIsLeaking: (isLeaking) => set(() => ({ isLeaking })),
  updateIssueType: (issueType) => set(() => ({ issueType })),
  resetIssueDetails: () =>
    set(() => ({
      isInvolvedInAccident: {
        isInvolvedInAccident: null,
        hasContactedPolice: null,
      },
      isLeftInDangerousPosition: {
        isLeftInDangerousPosition: null,
        hasContactedPolice: null,
      },
      issueType: {
        issueType: null,
        additionalInfo: '',
      },
      isLeaking: null,
      serviceLocationDetails: emptyServiceLocationDetails,
      issueLength: null,
      vehicleDetails: {
        registrationNumber: '',
        make: '',
        model: '',
        colour: '',
      },
      isPhotoLocationStored: false,
      uploadedImages: [],
      uploadedImagesFileName: [],
      uploadedImagesFileType: [],
      uploadedImagesBlob: [],
      fileDetailsObjects: [],
    })),
});
